/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseCrudService, API_CONFIG, HttpService } from '@nebula-shared/data-access';

export interface ArticuloDto {
  id: number;
  codigo: string;
  nombre: string;
  descripcion?: string;
  idTipoArticulo?: number;
  nombreTipoArticulo?: string;
  idUnidadMedida?: number;
  nombreUnidadMedida?: string;
  precio?: number;
  costo?: number;
  stock?: number;
  status: string;
  clientId?: number;
  companyId?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  version?: number;
}

export interface PageResponseArticulo {
  content: ArticuloDto[];
  size: number;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class ArticuloService extends BaseCrudService<any, any, any> {
  readonly httpService = inject(HttpService);

  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}${config.endpoints.articulos || '/articulos'}`);
  }

  /**
   * Obtener artículos paginados - SIN filtro de estado automático
   */
  getArticulosPage(
    page = 0,
    size = 20,
    searchTerm?: string,
    filters?: { tipoArticuloId?: number },
  ): Observable<PageResponseArticulo> {
    const searchsBy: any[] = [];

    searchsBy.push({
      key: 'status',
      operation: 'EQUAL',
      value: 'ACTIVE',
    });

    if (filters?.tipoArticuloId) {
      searchsBy.push({
        key: 'tipoArticuloId',
        operation: 'EQUAL',
        value: filters.tipoArticuloId,
      });
    }

    if (searchTerm) {
      searchsBy.push({
        key: 'codigo',
        operation: 'MATCH',
        value: searchTerm,
      });
    }

    const body = {
      page: page,
      size: size,
      searchsBy: searchsBy,
      ordersBy: [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page`, body).pipe(
      map((response: any) => {
        return this.mapToPageResponse(response);
      }),
    );
  }

  /**
   * Buscar un artículo específico por código
   */
  getArticuloByCodigo(codigo: string): Observable<ArticuloDto | null> {
    const body = {
      page: 0,
      size: 10,
      searchsBy: [
        {
          key: 'status',
          operation: 'EQUAL',
          value: 'ACTIVE',
        },
        {
          key: 'codigo',
          operation: 'EQUAL',
          value: codigo,
        },
      ],
      ordersBy: [{ column: 'codigo', direction: 'ASC' }],
    };

    return this.httpService.post<any>(`${this.baseUrl}/page`, body).pipe(
      map((response: any) => {
        const content = this.extractContent(response);
        return content.length > 0 ? this.mapToDto(content[0]) : null;
      }),
    );
  }

  private extractContent(response: any): any[] {
    if (response?.response?.content?.content && Array.isArray(response.response.content.content)) {
      return response.response.content.content;
    }

    if (response?.response?.content && Array.isArray(response.response.content)) {
      return response.response.content;
    }

    if (response?.content?.content && Array.isArray(response.content.content)) {
      return response.content.content;
    }

    if (response?.content && Array.isArray(response.content)) {
      return response.content;
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (response?.data && Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  }

  private extractPageMetadata(response: any): {
    size: number;
    number: number;
    numberOfElements: number;
    totalElements: number;
    totalPages: number;
  } {
    if (response?.response?.content && typeof response.response.content === 'object') {
      const metadata = response.response.content;
      return {
        size: metadata.size || 0,
        number: metadata.number || 0,
        numberOfElements: metadata.numberOfElements || 0,
        totalElements: metadata.totalElements || 0,
        totalPages: metadata.totalPages || 0,
      };
    }

    if (
      response?.content &&
      typeof response.content === 'object' &&
      !Array.isArray(response.content)
    ) {
      return {
        size: response.content.size || 0,
        number: response.content.number || 0,
        numberOfElements: response.content.numberOfElements || 0,
        totalElements: response.content.totalElements || 0,
        totalPages: response.content.totalPages || 0,
      };
    }

    return {
      size: response.size || 0,
      number: response.number || 0,
      numberOfElements: response.numberOfElements || 0,
      totalElements: response.totalElements || 0,
      totalPages: response.totalPages || 0,
    };
  }

  private mapToPageResponse(response: any): PageResponseArticulo {
    const content = this.extractContent(response);
    const metadata = this.extractPageMetadata(response);

    return {
      content: content.map((item: any) => this.mapToDto(item)),
      size: metadata.size,
      number: metadata.number,
      numberOfElements: metadata.numberOfElements,
      totalElements: metadata.totalElements,
      totalPages: metadata.totalPages,
      empty: content.length === 0,
    };
  }

  private mapToDto(item: any): ArticuloDto {
    let status = item.status || item.estado;
    if (status === 'accounting.articulo.estado.activo') status = 'ACTIVE';
    if (status === 'accounting.articulo.estado.inactivo') status = 'INACTIVE';
    if (status === 'accounting.articulo.estado.bloqueado') status = 'BLOCKED';

    return {
      id: item.id,
      codigo: item.codigo,
      nombre: item.nombre,
      descripcion: item.descripcion,
      idTipoArticulo: item.tipoArticuloId || item.idTipoArticulo,
      nombreTipoArticulo: item.tipoArticuloNombre || item.nombreTipoArticulo,
      idUnidadMedida: item.unidadMedidaId || item.idUnidadMedida,
      nombreUnidadMedida: item.unidadMedidaNombre || item.nombreUnidadMedida,
      precio: item.precio,
      costo: item.costo,
      stock: item.stock,
      status: status,
      clientId: item.clientId,
      companyId: item.companyId,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      createdBy: item.createdBy,
      updatedBy: item.updatedBy,
      version: item.version,
    };
  }
}
